import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@evertrue/et-components';
import EmailStepCard from './step-components/email-step-card';
import TaskStepCard from './step-components/task-step-card';
import ConditionStepCard from './step-components/condition-step-card';
import WaitStep from './step-components/wait-step';
import ScheduleStepCard from './step-components/schedule-step-card';
import AddStepModal from './modals/add-step-modal';
import AddConditionModal from './modals/add-condition-modal';
import AddTaskTypeModal from './modals/add-task-type-modal';

/**
 * Cadence flow editor for building email cadences.
 * Supports 1-level branching for condition steps with TRUE/FALSE paths.
 * Branches store arrays of steps (email, wait, exit).
 * Conditions cannot be nested within branches.
 */
const CadenceFlowEditor = ({
  steps,
  onStepsChange,
  selectedStepIndex,
  onStepSelect,
  selectedBranch,
  onBranchSelect,
  onCloseDrawer,
  domainStatus,
  isOwner,
  flowLocked,
}) => {
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [showTaskTypeModal, setShowTaskTypeModal] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [insertAtIndex, setInsertAtIndex] = useState(null);
  // Separate index for condition insertion — survives add-step modal close
  const [conditionInsertIndex, setConditionInsertIndex] = useState(null);
  // Separate index for task insertion — survives add-step modal close
  const [taskInsertIndex, setTaskInsertIndex] = useState(null);
  // Type of the nearest non-wait step before the condition being added/edited (used to restrict condition options)
  const [previousStepType, setPreviousStepType] = useState(null);
  // Track if we're adding a step to a branch
  const [branchInsertContext, setBranchInsertContext] = useState(null);
  // Track branch context for task insertion (survives modal close)
  const [taskBranchInsertContext, setTaskBranchInsertContext] = useState(null);

  const generateStepId = () => `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Walk backwards through stepList (skipping wait steps) to find the nearest actionable step type.
  const getPreviousNonWaitStepType = (stepList, beforeIndex) => {
    for (let i = beforeIndex - 1; i >= 0; i--) {
      if (stepList[i]?.type !== 'wait') return stepList[i].type;
    }
    return null;
  };

  const handleAddStep = (stepType) => {
    const newStep = {
      id: generateStepId(),
      type: stepType.id,
    };

    if (stepType.id === 'email') {
      Object.assign(newStep, {
        template: null,
        subject: '',
        body: '',
        isAutomated: true,
      });
    } else if (stepType.id === 'task') {
      // Save context before closing the add-step modal, then open task type picker.
      const insertIdx = insertAtIndex !== null ? insertAtIndex : steps.length;
      setTaskInsertIndex(insertIdx);
      setTaskBranchInsertContext(branchInsertContext);
      setInsertAtIndex(null);
      setBranchInsertContext(null);
      setShowAddStepModal(false);
      setShowTaskTypeModal(true);
      return;
    } else if (stepType.id === 'wait') {
      Object.assign(newStep, {
        waitDuration: 3,
        waitUnit: 'days',
      });
    } else if (stepType.id === 'exit') {
      // Exit step - contact leaves the cadence
      Object.assign(newStep, {
        exitReason: 'Branch exit',
      });
    } else if (stepType.id === 'condition') {
      // Save the insertion index before closing the add-step modal.
      // The add-step modal's onClose resets insertAtIndex, so we store
      // it separately so the condition lands at the correct position.
      const insertIdx = insertAtIndex !== null ? insertAtIndex : steps.length;
      setConditionInsertIndex(insertIdx);
      setPreviousStepType(getPreviousNonWaitStepType(steps, insertIdx));
      setShowAddStepModal(false);
      setShowConditionModal(true);
      return;
    }

    // Check if we're adding to a branch
    if (branchInsertContext) {
      const { stepIndex, branchKey, insertAt } = branchInsertContext;
      const newSteps = [...steps];
      const branchSteps = [...(newSteps[stepIndex].branches[branchKey] || [])];
      branchSteps.splice(insertAt, 0, newStep);
      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        branches: {
          ...newSteps[stepIndex].branches,
          [branchKey]: branchSteps,
        },
      };
      onStepsChange(newSteps);
      if (onBranchSelect) onBranchSelect({ stepIndex, branchKey, branchStepIndex: insertAt });
      setBranchInsertContext(null);
    } else {
      const insertIndex = insertAtIndex !== null ? insertAtIndex : steps.length;
      const newSteps = [...steps];
      newSteps.splice(insertIndex, 0, newStep);
      onStepsChange(newSteps);
      if (onStepSelect) onStepSelect(insertIndex);
      setInsertAtIndex(null);
    }
  };

  /**
   * Creates a condition step with empty TRUE/FALSE branch arrays.
   */
  const handleAddCondition = (condition) => {
    if (editingStepIndex !== null && steps[editingStepIndex]?.type === 'condition') {
      const newSteps = [...steps];
      newSteps[editingStepIndex] = {
        ...newSteps[editingStepIndex],
        condition,
      };
      onStepsChange(newSteps);
      setEditingStepIndex(null);
      return;
    }

    const newStep = {
      id: generateStepId(),
      type: 'condition',
      condition,
      branches: {
        true: [],
        false: [],
      },
    };

    const insertIndex = conditionInsertIndex !== null ? conditionInsertIndex : steps.length;
    const newSteps = [...steps];

    // Auto-add a 3-day wait step before the condition only if the preceding
    // step is not already a wait (NEXDEV-799).
    const precedingStep = insertIndex > 0 ? newSteps[insertIndex - 1] : null;
    if (precedingStep?.type === 'wait') {
      newSteps.splice(insertIndex, 0, newStep);
    } else {
      const waitStep = {
        id: generateStepId(),
        type: 'wait',
        waitDuration: 3,
        waitUnit: 'days',
      };
      newSteps.splice(insertIndex, 0, waitStep, newStep);
    }

    onStepsChange(newSteps);
    setConditionInsertIndex(null);
    if (onCloseDrawer) onCloseDrawer();
  };

  /**
   * Adds a task step with the selected task type after the task type picker is confirmed.
   */
  const handleAddTaskStep = (taskTypeInfo) => {
    const newStep = {
      id: generateStepId(),
      type: 'task',
      taskType: taskTypeInfo.id,
      name: '',
      description: '',
    };

    if (taskBranchInsertContext) {
      const { stepIndex, branchKey, insertAt } = taskBranchInsertContext;
      const newSteps = [...steps];
      const branchSteps = [...(newSteps[stepIndex].branches[branchKey] || [])];
      branchSteps.splice(insertAt, 0, newStep);
      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        branches: { ...newSteps[stepIndex].branches, [branchKey]: branchSteps },
      };
      onStepsChange(newSteps);
      if (onBranchSelect) onBranchSelect({ stepIndex, branchKey, branchStepIndex: insertAt });
    } else {
      const insertIdx = taskInsertIndex !== null ? taskInsertIndex : steps.length;
      const newSteps = [...steps];
      newSteps.splice(insertIdx, 0, newStep);
      onStepsChange(newSteps);
      if (onStepSelect) onStepSelect(insertIdx);
    }

    setTaskInsertIndex(null);
    setTaskBranchInsertContext(null);
  };

  const handleUpdateStep = (index, updatedStep) => {
    const newSteps = [...steps];
    newSteps[index] = updatedStep;
    onStepsChange(newSteps);
  };

  const handleDeleteStep = (index) => {
    const newSteps = steps.filter((_, i) => i !== index);
    onStepsChange(newSteps);
    if (onBranchSelect) onBranchSelect(null);
  };

  /**
   * Deletes a step from a branch.
   */
  const handleDeleteBranchStep = (stepIndex, branchKey, branchStepIndex) => {
    const newSteps = [...steps];
    const branchSteps = newSteps[stepIndex].branches[branchKey].filter((_, i) => i !== branchStepIndex);
    newSteps[stepIndex] = {
      ...newSteps[stepIndex],
      branches: {
        ...newSteps[stepIndex].branches,
        [branchKey]: branchSteps,
      },
    };
    onStepsChange(newSteps);
  };

  const openAddStepAtIndex = (index) => {
    setInsertAtIndex(index);
    setBranchInsertContext(null);
    setShowAddStepModal(true);
  };

  /**
   * Opens modal to add step within a branch (no condition option).
   */
  const openAddBranchStep = (stepIndex, branchKey, insertAt) => {
    setBranchInsertContext({ stepIndex, branchKey, insertAt });
    setInsertAtIndex(null);
    setShowAddStepModal(true);
  };

  const handleBranchStepClick = (stepIndex, branchKey, branchStepIndex) => {
    if (onBranchSelect) {
      onBranchSelect({ stepIndex, branchKey, branchStepIndex });
    }
  };

  const isBranchStepSelected = (stepIndex, branchKey, branchStepIndex) => {
    return (
      selectedBranch &&
      selectedBranch.stepIndex === stepIndex &&
      selectedBranch.branchKey === branchKey &&
      selectedBranch.branchStepIndex === branchStepIndex
    );
  };

  /**
   * Renders a plus button to add steps within a branch.
   */
  const renderBranchAddButton = (stepIndex, branchKey, insertAt) => {
    if (flowLocked) return null;
    return (
      <div className="cadence-flow-editor--branch-add">
        <button
          className="cadence-flow-editor--add-btn cadence-flow-editor--add-btn-small"
          onClick={() => openAddBranchStep(stepIndex, branchKey, insertAt)}
          title="Add step"
        >
          <Icon icon="add" />
        </button>
      </div>
    );
  };

  /**
   * Renders a step within a branch.
   */
  const renderBranchStepItem = (branchStep, stepIndex, branchKey, branchStepIndex, stepNumber) => {
    if (branchStep.type === 'email') {
      return (
        <div
          key={branchStep.id}
          className={`cadence-flow-editor--branch-step-item ${isBranchStepSelected(stepIndex, branchKey, branchStepIndex) ? 'is-selected' : ''}`}
          onClick={() => handleBranchStepClick(stepIndex, branchKey, branchStepIndex)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleBranchStepClick(stepIndex, branchKey, branchStepIndex)}
        >
          <EmailStepCard
            step={branchStep}
            stepNumber={stepNumber}
            onDelete={() => handleDeleteBranchStep(stepIndex, branchKey, branchStepIndex)}
            onEditEmail={() => handleBranchStepClick(stepIndex, branchKey, branchStepIndex)}
            domainStatus={domainStatus}
            isOwner={isOwner}
          />
        </div>
      );
    }

    if (branchStep.type === 'task') {
      // Manual tasks are read-only on active/paused cadences — don't let the card
      // open the editing drawer, since task edits can't be persisted in those states.
      const taskClickProps = flowLocked
        ? {}
        : {
            onClick: () => handleBranchStepClick(stepIndex, branchKey, branchStepIndex),
            role: 'button',
            tabIndex: 0,
            onKeyDown: (e) => e.key === 'Enter' && handleBranchStepClick(stepIndex, branchKey, branchStepIndex),
          };
      return (
        <div
          key={branchStep.id}
          className={`cadence-flow-editor--branch-step-item ${isBranchStepSelected(stepIndex, branchKey, branchStepIndex) ? 'is-selected' : ''} ${flowLocked ? 'is-locked' : ''}`}
          {...taskClickProps}
        >
          <TaskStepCard
            step={branchStep}
            stepNumber={stepNumber}
            onDelete={() => handleDeleteBranchStep(stepIndex, branchKey, branchStepIndex)}
            onEdit={() => handleBranchStepClick(stepIndex, branchKey, branchStepIndex)}
            flowLocked={flowLocked}
          />
        </div>
      );
    }

    if (branchStep.type === 'wait') {
      return (
        <div key={branchStep.id} className="cadence-flow-editor--branch-step-item">
          <WaitStep
            waitTime={{ duration: branchStep.waitDuration, unit: branchStep.waitUnit }}
            onEdit={() => handleBranchStepClick(stepIndex, branchKey, branchStepIndex)}
            onDelete={() => handleDeleteBranchStep(stepIndex, branchKey, branchStepIndex)}
          />
        </div>
      );
    }

    if (branchStep.type === 'exit') {
      return (
        <div key={branchStep.id} className="cadence-flow-editor--branch-step-item">
          <div className="step-card step-card--exit">
            <div className="step-card--header">
              <div className="step-card--title-row">
                <div className="step-card--icon-wrapper step-card--icon-exit">
                  <Icon icon="exit-to-app" />
                </div>
                <div className="step-card--title-content">
                  <span className="step-card--title">Exit Cadence</span>
                  <span className="step-card--placeholder-hint">Contact exits here</span>
                </div>
              </div>
              <div className="step-card--header-actions">
                <Button
                  type="simple"
                  onClick={() => handleDeleteBranchStep(stepIndex, branchKey, branchStepIndex)}
                  title="Delete step"
                >
                  <Icon icon="delete" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  /**
   * Checks if a branch ends with an exit step.
   */
  const branchHasExit = (branchSteps) => {
    return branchSteps.length > 0 && branchSteps[branchSteps.length - 1].type === 'exit';
  };

  /**
   * Renders the branches for a condition step - simplified with plus signs.
   */
  const renderBranches = (step, stepIndex) => {
    if (!step.branches) return null;

    const parentNumber = stepIndex + 1;
    const trueBranch = step.branches.true || [];
    const falseBranch = step.branches.false || [];
    const trueHasExit = branchHasExit(trueBranch);
    const falseHasExit = branchHasExit(falseBranch);

    return (
      <div className="cadence-flow-editor--branch-container">
        <div className="cadence-flow-editor--fork-connector" />

        <div className="cadence-flow-editor--branch-columns">
          {/* TRUE branch */}
          <div className="cadence-flow-editor--branch cadence-flow-editor--branch-true">
            <span className="cadence-flow-editor--branch-label cadence-flow-editor--branch-label-true">TRUE</span>
            <div className="cadence-flow-editor--branch-connector" />
            <div className="cadence-flow-editor--branch-steps">
              {renderBranchAddButton(stepIndex, 'true', 0)}
              {trueBranch.map((branchStep, i) => (
                <div key={branchStep.id}>
                  {renderBranchStepItem(branchStep, stepIndex, 'true', i, `${parentNumber}A${i + 1}`)}
                  {!branchHasExit([branchStep]) && renderBranchAddButton(stepIndex, 'true', i + 1)}
                </div>
              ))}
              {trueHasExit && (
                <div className="cadence-flow-editor--branch-exit-marker">
                  <Icon icon="block" />
                </div>
              )}
            </div>
          </div>

          {/* FALSE branch */}
          <div className="cadence-flow-editor--branch cadence-flow-editor--branch-false">
            <span className="cadence-flow-editor--branch-label cadence-flow-editor--branch-label-false">FALSE</span>
            <div className="cadence-flow-editor--branch-connector" />
            <div className="cadence-flow-editor--branch-steps">
              {renderBranchAddButton(stepIndex, 'false', 0)}
              {falseBranch.map((branchStep, i) => (
                <div key={branchStep.id}>
                  {renderBranchStepItem(branchStep, stepIndex, 'false', i, `${parentNumber}B${i + 1}`)}
                  {!branchHasExit([branchStep]) && renderBranchAddButton(stepIndex, 'false', i + 1)}
                </div>
              ))}
              {falseHasExit && (
                <div className="cadence-flow-editor--branch-exit-marker">
                  <Icon icon="block" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Only show merge if at least one branch doesn't exit */}
        {(!trueHasExit || !falseHasExit) && <div className="cadence-flow-editor--merge-connector" />}
      </div>
    );
  };

  const renderAddStepButton = (index) => {
    if (flowLocked) return null;
    return (
      <div key={`add-${index}`} className="cadence-flow-editor--add-step">
        <button className="cadence-flow-editor--add-btn" onClick={() => openAddStepAtIndex(index)} title="Add step">
          <Icon icon="add" />
        </button>
      </div>
    );
  };

  /**
   * Get step type IDs available based on context (main flow vs branch).
   */
  const getAvailableStepTypes = () => {
    if (branchInsertContext) {
      // In a branch - add Exit option, no Condition
      return ['email', 'task', 'wait', 'exit'];
    }
    // Main flow - add Condition
    return ['email', 'task', 'wait', 'condition'];
  };

  return (
    <div className="cadence-flow-editor">
      <div className="cadence-flow-editor--canvas">
        {steps.length === 0 ? (
          <div className="cadence-flow-editor--empty-steps">
            <div className="cadence-flow-editor--add-step-cta">
              <Button type="secondary" onClick={() => setShowAddStepModal(true)}>
                <Icon icon="add" /> Add Step
              </Button>
              <p>Add steps to build your cadence</p>
            </div>
          </div>
        ) : (
          <>
            {steps.map((step, index) => {
              // Manual tasks are read-only on active/paused cadences — the wrapper must
              // not open the editing drawer, since task edits can't be persisted then.
              const stepLocked = flowLocked && step.type === 'task';
              const stepClickProps = stepLocked
                ? {}
                : {
                    onClick: () => onStepSelect && onStepSelect(index),
                    role: 'button',
                    tabIndex: 0,
                    onKeyDown: (e) => e.key === 'Enter' && onStepSelect && onStepSelect(index),
                  };
              return (
                <div key={step.id} className="cadence-flow-editor--step-wrapper">
                  {renderAddStepButton(index)}
                  <div className="cadence-flow-editor--connector" />
                  <div
                    className={`cadence-flow-editor--step-clickable ${selectedStepIndex === index && !selectedBranch ? 'is-selected' : ''} ${stepLocked ? 'is-locked' : ''}`}
                    {...stepClickProps}
                  >
                    {step.type === 'email' && (
                      <EmailStepCard
                        step={step}
                        stepNumber={index + 1}
                        onDelete={() => handleDeleteStep(index)}
                        onEditEmail={() => onStepSelect && onStepSelect(index)}
                        domainStatus={domainStatus}
                        isOwner={isOwner}
                      />
                    )}
                    {step.type === 'task' && (
                      <TaskStepCard
                        step={step}
                        stepNumber={index + 1}
                        onDelete={() => handleDeleteStep(index)}
                        onEdit={() => onStepSelect && onStepSelect(index)}
                        flowLocked={flowLocked}
                      />
                    )}
                    {step.type === 'condition' && (
                      <ConditionStepCard
                        condition={step.condition}
                        onEdit={() => {
                          setEditingStepIndex(index);
                          setPreviousStepType(getPreviousNonWaitStepType(steps, index));
                          setShowConditionModal(true);
                        }}
                        onDelete={() => handleDeleteStep(index)}
                      />
                    )}
                    {step.type === 'wait' && (
                      <WaitStep
                        waitTime={{ duration: step.waitDuration, unit: step.waitUnit }}
                        onEdit={() => onStepSelect && onStepSelect(index)}
                        onDelete={() => handleDeleteStep(index)}
                      />
                    )}
                    {step.type === 'schedule' && (
                      <ScheduleStepCard
                        step={step}
                        onUpdate={(updated) => handleUpdateStep(index, updated)}
                        onDelete={() => handleDeleteStep(index)}
                      />
                    )}
                  </div>

                  {step.type === 'condition' && step.branches && renderBranches(step, index)}

                  <div className="cadence-flow-editor--connector" />
                </div>
              );
            })}
            {renderAddStepButton(steps.length)}
          </>
        )}

        <div className="cadence-flow-editor--connector" />
        <div className="cadence-flow-editor--end-node">
          <Icon icon="flag" />
          <span>End</span>
        </div>
      </div>

      <AddStepModal
        isOpen={showAddStepModal}
        onClose={() => {
          setShowAddStepModal(false);
          setInsertAtIndex(null);
          setBranchInsertContext(null);
        }}
        onSelectStepType={handleAddStep}
        availableTypes={getAvailableStepTypes()}
        isBranchContext={!!branchInsertContext}
      />

      <AddConditionModal
        isOpen={showConditionModal}
        onClose={() => {
          setShowConditionModal(false);
          setConditionInsertIndex(null);
          setEditingStepIndex(null);
          setPreviousStepType(null);
        }}
        onAddCondition={handleAddCondition}
        previousStepType={previousStepType}
        existingCondition={
          editingStepIndex !== null && steps[editingStepIndex]?.type === 'condition'
            ? steps[editingStepIndex].condition
            : null
        }
      />

      <AddTaskTypeModal
        isOpen={showTaskTypeModal}
        onClose={() => {
          setShowTaskTypeModal(false);
          setTaskInsertIndex(null);
          setTaskBranchInsertContext(null);
        }}
        onSelectTaskType={handleAddTaskStep}
      />
    </div>
  );
};

CadenceFlowEditor.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      type: PropTypes.string.isRequired,
      template: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      subject: PropTypes.string,
      body: PropTypes.string,
      waitDuration: PropTypes.number,
      waitUnit: PropTypes.string,
      condition: PropTypes.object,
      branches: PropTypes.shape({
        true: PropTypes.array,
        false: PropTypes.array,
      }),
    })
  ).isRequired,
  onStepsChange: PropTypes.func.isRequired,
  selectedStepIndex: PropTypes.number,
  onStepSelect: PropTypes.func,
  selectedBranch: PropTypes.shape({
    stepIndex: PropTypes.number.isRequired,
    branchKey: PropTypes.string.isRequired,
    branchStepIndex: PropTypes.number,
  }),
  onBranchSelect: PropTypes.func,
  onCloseDrawer: PropTypes.func,
  domainStatus: PropTypes.oneOf(['not_configured', 'pending', 'configured']),
  isOwner: PropTypes.bool,
  flowLocked: PropTypes.bool,
};

export default CadenceFlowEditor;
